import { createContext, memo, type ReactNode, useContext, useMemo, useState } from 'react';

import { reportErrorToBugsnag } from '@air/utils-error';

import { isDevOrTestStage } from '~/swr-hooks/utils';

export type ImageSearchContextType = {
  isExpanded: boolean;
  setIsExpanded: (expanded: boolean) => void;
};

const defaultValue: ImageSearchContextType = {
  isExpanded: false,
  setIsExpanded: () => {},
};

const ImageSearchContext = createContext<ImageSearchContextType>(defaultValue);

type ImageSearchContextProviderProps = {
  children: ReactNode;
};

export const ImageSearchContextProvider = memo(({ children }: ImageSearchContextProviderProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const value = useMemo<ImageSearchContextType>(
    () => ({
      isExpanded,
      setIsExpanded,
    }),
    [isExpanded],
  );

  return <ImageSearchContext.Provider value={value}>{children}</ImageSearchContext.Provider>;
});

ImageSearchContextProvider.displayName = 'ImageSearchContextProvider';

export const useImageSearchContext = (): ImageSearchContextType => {
  const context = useContext(ImageSearchContext);

  if (context === defaultValue) {
    const error = new Error('useImageSearchContext must be used within an ImageSearchContextProvider');
    // Context not available, return default value instead of throwing
    if (isDevOrTestStage()) {
      throw error;
    } else {
      reportErrorToBugsnag({
        error,
        context: 'ImageSearchContext not found',
      });
    }
  }

  return context;
};
